import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Records a voice note with the device microphone and hands it back as a
 * File, the same shape as a dropped recording, so the rest of the flow does
 * not care whether the agent talked or uploaded.
 *
 * The browser picks the container it can produce (WebM/Opus on Chrome and
 * Android, MP4/AAC on Safari and iPhone); the API converts either to MP3
 * before the model listens. The composer sends it to be written out word for
 * word, mixed English, Malay and Chinese kept as spoken, and puts the text in
 * the box; a dropped recording is read by the model as audio instead.
 */
export const MAX_RECORDING_SECONDS = 5 * 60

const CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg']

const pickMimeType = () => {
    if (typeof MediaRecorder === 'undefined') return null
    return CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) ?? ''
}

const extensionFor = (mimeType: string) =>
    mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : mimeType.includes('wav') ? 'wav' : 'webm'

export type RecorderStatus = 'idle' | 'starting' | 'recording' | 'unsupported'

export interface VoiceRecorder {
    status: RecorderStatus
    /** Seconds recorded so far, while recording. */
    seconds: number
    error: string | null
    start: () => Promise<void>
    /** Stops and resolves with the recording, or null if nothing was captured. */
    stop: () => Promise<File | null>
    cancel: () => void
}

let sequence = 0

export function useVoiceRecorder(): VoiceRecorder {
    const [status, setStatus] = useState<RecorderStatus>(() =>
        typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function' && typeof MediaRecorder !== 'undefined'
            ? 'idle'
            : 'unsupported',
    )
    const [seconds, setSeconds] = useState(0)
    const [error, setError] = useState<string | null>(null)
    const recorder = useRef<MediaRecorder | null>(null)
    const stream = useRef<MediaStream | null>(null)
    const chunks = useRef<Blob[]>([])
    const timer = useRef<number | null>(null)
    const startedAt = useRef(0)
    const stopResolver = useRef<((file: File | null) => void) | null>(null)

    const release = useCallback(() => {
        if (timer.current) window.clearInterval(timer.current)
        timer.current = null
        stream.current?.getTracks().forEach((t) => t.stop())
        stream.current = null
        recorder.current = null
        setSeconds(0)
    }, [])

    const finish = useCallback(() => {
        const rec = recorder.current
        const type = (rec?.mimeType || pickMimeType() || 'audio/webm').split(';')[0]
        const blob = new Blob(chunks.current, { type })
        chunks.current = []
        const tooShort = Date.now() - startedAt.current < 700
        release()
        setStatus('idle')
        const file =
            blob.size > 0 && !tooShort
                ? new File([blob], `voice-note-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}-${(sequence += 1)}.${extensionFor(type)}`, { type })
                : null
        if (!file && tooShort) setError('That was too short to keep. Hold the mic a little longer.')
        stopResolver.current?.(file)
        stopResolver.current = null
    }, [release])

    const start = useCallback(async () => {
        if (status === 'unsupported' || status === 'recording' || status === 'starting') return
        setError(null)
        setStatus('starting')
        try {
            const media = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
            const mimeType = pickMimeType()
            const rec = mimeType ? new MediaRecorder(media, { mimeType }) : new MediaRecorder(media)
            chunks.current = []
            rec.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.current.push(e.data)
            }
            rec.onstop = finish
            rec.onerror = () => {
                setError('Recording stopped unexpectedly. Try again.')
                release()
                setStatus('idle')
            }
            stream.current = media
            recorder.current = rec
            startedAt.current = Date.now()
            rec.start(1000)
            setStatus('recording')
            setSeconds(0)
            timer.current = window.setInterval(() => {
                const elapsed = Math.floor((Date.now() - startedAt.current) / 1000)
                setSeconds(elapsed)
                if (elapsed >= MAX_RECORDING_SECONDS && recorder.current?.state === 'recording') recorder.current.stop()
            }, 250)
        } catch (err: unknown) {
            const e = err as { name?: string }
            setError(
                e.name === 'NotAllowedError' || e.name === 'SecurityError'
                    ? 'Microphone access was blocked. Allow it in the browser settings, or upload a recording instead.'
                    : e.name === 'NotFoundError'
                      ? 'No microphone was found on this device.'
                      : 'The microphone could not be started.',
            )
            release()
            setStatus('idle')
        }
    }, [status, finish, release])

    const stop = useCallback(
        () =>
            new Promise<File | null>((resolve) => {
                const rec = recorder.current
                if (!rec || rec.state === 'inactive') {
                    resolve(null)
                    return
                }
                stopResolver.current = resolve
                rec.stop()
            }),
        [],
    )

    const cancel = useCallback(() => {
        const rec = recorder.current
        stopResolver.current = null
        chunks.current = []
        if (rec && rec.state !== 'inactive') {
            rec.onstop = null
            rec.stop()
        }
        release()
        setStatus('idle')
    }, [release])

    useEffect(() => cancel, [cancel])

    return { status, seconds, error, start, stop, cancel }
}

export const formatSeconds = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
